import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { calculateDay, extensionName, isEmpty } from '../../helpers/common';
import {
  EMPTY_STATE_SIZE,
  FILE_EXTENSIONS,
  FILE_TYPES,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE
} from '../../global/constants';
import {
  BreadCrumb,
  BreadCrumbContent,
  BreadSlash,
  BredCrumbBlock,
  Card,
  CardDecs,
  CardTitle,
  FileIconDiv,
  FolderDiv,
  FolderRight,
  HeaderText,
  Icon,
  LeftCard,
  MainDiv,
  RightCard,
  RightHeader
} from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import FileSVGIcon from '../../assets/images/svg/filesIconSvg';
import { rootStore } from '../../mobx/rootStore';
import { RawProjectFile } from '../../mobx/interfaces/project';
import { observer } from 'mobx-react-lite';
import { getProjectFiles } from '../../services/projectServices';
import { ProjectModel } from '../../mobx/models/project';
import { useLocation } from 'react-router-dom';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { useMobileDevice } from '../../global/useMobile';
import ResponsiveHeader from '../responsiveHeader';
import IconButton from '../button/iconButton';
import EmptyStateV2 from '../emptyState';
import { FileType } from '../fileUploadCard/styles';
interface Props {
  setAttachment?: () => void;
  setSelectFile?: (value: boolean) => void;
  onSelectFile?: (value: RawProjectFile) => void;
  isMessage?: boolean;
  isCreateTaskModal?: boolean;
  isProjectMessageDetail?: boolean;
  isProjectOverview?: boolean;
  removeMargin?: boolean;
  hasBottomPadding?: boolean;
}

const RenderExistingFile: React.FC<Props> = (props) => {
  const {
    setAttachment,
    setSelectFile,
    onSelectFile,
    isProjectOverview,
    isCreateTaskModal,
    isMessage,
    removeMargin,
    hasBottomPadding,
    isProjectMessageDetail
  } = props;
  //States
  const [path, setPath] = useState<RawProjectFile[]>([]);
  //Mobx store
  const { projectStore, taskStore } = rootStore;
  const { currentProject } = projectStore;
  const { createTaskInput } = taskStore;
  //Other variable
  const isMobile = useMobileDevice();
  const chatGroupId =
    new URLSearchParams(useLocation().search).get('chatGroup') ||
    UserPreferenceSingleton.getInstance().getLastMessageGroup();

  const projectDetails = useMemo(() => {
    return isCreateTaskModal
      ? createTaskInput?.project instanceof ProjectModel
        ? createTaskInput?.project
        : null
      : currentProject;
  }, [isCreateTaskModal, createTaskInput?.project, currentProject]);

  const loadData = useCallback(async () => {
    if (!projectDetails?.id) return;
    const { files, id } = projectDetails;

    // Files already loaded for this project: create task syncs in the background (it no longer prefetches on modal open), others skip.
    if (files?.length && files[0]?.projectId === id) {
      if (isCreateTaskModal) await getProjectFiles(id);
      return;
    }

    projectDetails.setCurrentFolderId(null);
    if (isMessage) {
      rootStore.projectStore.setCurrentProject(chatGroupId as string);
    }
    // Clear any previous project's files first so the picker doesn't flash stale data while loading.
    projectDetails.setProjectFiles([]);
    await getProjectFiles(id);
  }, [chatGroupId, isCreateTaskModal, isMessage, projectDetails]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Keep the breadcrumb in sync when the folder is reset to root from outside (e.g. reopening the picker).
  useEffect(() => {
    if (!projectDetails?.currentFolderId) setPath([]);
  }, [projectDetails?.currentFolderId]);

  const onClickFilesText = useCallback(() => {
    projectDetails?.setCurrentFolderId(null);
    setPath([]);
  }, [projectDetails]);

  const onClickPath = useCallback(
    (e: React.SyntheticEvent, item: RawProjectFile) => {
      e.stopPropagation();
      projectDetails?.setCurrentFolderId(item?.id);
      const itemIndex = path?.findIndex((x) => x?.id === item?.id);
      let pathClone = path;
      const ref = pathClone?.slice(0, itemIndex + 1);
      setPath(ref);
    },
    [path, projectDetails]
  );

  const closeFileAttach = useCallback(() => {
    setPath([]);
    setAttachment?.();
    setSelectFile?.(false);
  }, [setAttachment, setSelectFile]);

  const onClickFile = useCallback(
    (e: React.SyntheticEvent, item: RawProjectFile) => {
      e.stopPropagation();
      if (item?.uploadFileType === FILE_TYPES.FOLDER) {
        projectDetails?.setCurrentFolderId(item?.id);
        const updatedPath = [...path, item];
        setPath(updatedPath);
      } else {
        onSelectFile?.(item);
        setSelectFile?.(false);
      }
    },
    [projectDetails, path, onSelectFile, setSelectFile]
  );

  const renderCloseIcon = useCallback(() => {
    return (
      <IconButton
        iconName='new-close-icon'
        size={isMobile ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.EXTRA_SMALL}
        variant={ICON_BUTTON_TYPE.TERTIARY}
        onClick={closeFileAttach}
      />
    );
  }, [closeFileAttach, isMobile]);

  return (
    <>
      <MainDiv>
        <FolderRight
          isProjectOverview={isProjectOverview}
          isMessageDetail={isMessage || isProjectMessageDetail}
          removeMargin={removeMargin}
          className={
            isProjectOverview || isProjectMessageDetail || isMessage
              ? 'desktop-overview-file-select-drawer'
              : 'desktop-file-select-drawer'
          }>
          {!isMobile ? (
            <RightHeader>
              <HeaderText>Select File to Attach</HeaderText>
              {renderCloseIcon()}
            </RightHeader>
          ) : (
            <ResponsiveHeader title='Select File to Attach' noMargin={true} leftElement={renderCloseIcon()} />
          )}
          {isEmpty(projectDetails?.files) && (
            <EmptyStateV2
              size={EMPTY_STATE_SIZE.SMALL}
              title='You have no Files'
              imageUrl='files_list_empty'
              isAlignedCenter={true}
            />
          )}
          {path?.length > 0 && (
            <BreadCrumb>
              <Icon>
                <SVGIcon
                  name='filepath-folder-icon'
                  width={ICON_SIZE.XXL}
                  height={ICON_SIZE.XXL}
                  viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                />
              </Icon>
              <BreadCrumbContent>
                <BredCrumbBlock role='button' onClick={onClickFilesText}>
                  Files
                </BredCrumbBlock>
                {path?.map((item, index: number) => {
                  return (
                    <React.Fragment key={item?.id}>
                      <BreadSlash>/</BreadSlash>
                      <BredCrumbBlock
                        role='button'
                        isActive={index === path?.length - 1}
                        onClick={(e) => onClickPath(e, item)}>
                        {item?.name}
                      </BredCrumbBlock>
                    </React.Fragment>
                  );
                })}
              </BreadCrumbContent>
            </BreadCrumb>
          )}
          {!isEmpty(projectDetails?.folderWiseFiles) && (
            <FolderDiv hasBottomPadding={hasBottomPadding}>
              {projectDetails?.folderWiseFiles?.map((item) => {
                const extension = extensionName(item).toLowerCase();
                return (
                  <Card role='button' key={item.id} onClick={(e) => onClickFile(e, item)}>
                    <LeftCard>
                      {(!item?.uploadFileType || item?.uploadFileType === FILE_TYPES.UPLOAD) &&
                      !FILE_EXTENSIONS.includes(extension) ? (
                        <FileIconDiv>
                          <FileSVGIcon name={'blank'} height='36' width='36' viewBox='0 0 60 60' />
                          <FileType fontSize={extension?.length <= 5 ? 6 : 7} className='file-text'>
                            {extension?.split('.')?.pop()?.toUpperCase()}
                          </FileType>
                        </FileIconDiv>
                      ) : (
                        <FileSVGIcon
                          name={
                            !item?.uploadFileType || item?.uploadFileType === FILE_TYPES.UPLOAD
                              ? extension
                              : `icon_${item?.uploadFileType}`
                          }
                          height='36'
                          width='36'
                          viewBox='0 0 60 60'
                        />
                      )}
                    </LeftCard>
                    <RightCard>
                      <CardTitle>{item.name}</CardTitle>
                      <CardDecs>{`${calculateDay(item.createdOn)} by ${item?.user?.name}`}</CardDecs>
                    </RightCard>
                  </Card>
                );
              })}
            </FolderDiv>
          )}
        </FolderRight>
      </MainDiv>
    </>
  );
};

export default observer(RenderExistingFile);
